import React, { useContext, useState } from 'react';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Col,
  Descriptions,
  Row,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import AddRolesDialog from '@totem/components/services/remoteAccess/auth0users/details/AddRolesDialog';
import Auth0UserActions from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserActions';
import Auth0UserDetailsContext from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserDetailsContext';
import Auth0UserRolesTable from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserRolesTable';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const { Title, Text } = Typography;

const NEVER_LOGGED_IN = '0001-01-01T00:00:00Z';

const formatDate = (dateStr?: string): string => {
  if (!dateStr || dateStr === NEVER_LOGGED_IN) {
    return 'Never';
  }
  return new Date(dateStr).toLocaleString();
};

export const Auth0UserDetailsExtra = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { roles, onRefresh } = useContext(Auth0UserDetailsContext);
  const [showAddRoles, setShowAddRoles] = useState<boolean>(false);

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    securityGroupCheckConstraintAny,
  );

  return (
    <Space>
      <RecordCountBadge count={roles?.length ?? 0} />
      {isRemoteAccessAdmin && (
        <Button icon={<PlusOutlined />} onClick={() => setShowAddRoles(true)}>
          Add Roles
        </Button>
      )}
      <Auth0UserActions />
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
      <AddRolesDialog
        visible={showAddRoles}
        onClose={() => setShowAddRoles(false)}
      />
    </Space>
  );
};

const Auth0UserDetailsLayout = () => {
  const { loading, user } = useContext(Auth0UserDetailsContext);

  if (loading && !user) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  const item = user?.item;
  const lastLogin = formatDate(item?.last_login);

  return (
    <>
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Avatar size={64} shape="circle" src={item?.picture} />
        </Col>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {item?.name}
          </Title>
          <Text type="secondary">{item?.email}</Text>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Email Verified">
              {item?.email_verified ? (
                <Tag color="success">Yes</Tag>
              ) : (
                <Tag color="default">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Logins Count">
              {item?.logins_count ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Last Login">
              {lastLogin === 'Never' ? (
                <Tag color="warning">Never</Tag>
              ) : (
                lastLogin
              )}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} md={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Last Password Reset">
              {formatDate(item?.last_password_reset)}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {formatDate(item?.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated">
              {formatDate(item?.updated_at)}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>
        Roles
      </Title>
      <Auth0UserRolesTable />
    </>
  );
};

export default Auth0UserDetailsLayout;
